import {
  DownloadOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { Button, FormInstance, Image, message, Upload } from 'antd'
import type { UploadChangeParam } from 'antd/es/upload'
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface'
import { ButtonType } from 'antd/lib/button'
import { ILicenseMedia, INewFileList } from 'components/License'
import { Media } from 'interfaces/media'
import { useTranslations } from 'next-intl'
import { stringify } from 'qs'
import React, { useCallback, useEffect, useState } from 'react'
import axiosInstance from 'utils/axiosInstance'
import { getResponseData, getResponseErrorMessage } from 'utils/commonUtils'

interface IProps {
  setMedia: (data: Media | any) => void
  fieldName?: string
  form?: FormInstance
  maxCount?: number
  dataMedia?: ILicenseMedia[]
  isOnReview?: boolean
  isDownload?: boolean
  fileList?: INewFileList[]
  className?: string
  type?: ButtonType
  isShowMessage?: boolean
  disabled?: boolean
  isRemove?: boolean
  endPoint?: string
  size?: 'middle' | 'small' | 'large'
}
const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })

const MediaFileUpload: React.FC<IProps> = ({
  setMedia,
  fieldName,
  form,
  maxCount = 1,
  dataMedia,
  isOnReview = false,
  isDownload = false,
  disabled = false,
  fileList,
  className = 'mb-5',
  type = 'default',
  isShowMessage = true,
  endPoint = '/v1/media',
  size = 'middle',
}) => {
  const [loading, setLoading] = useState(false)
  const [nextFileList, setNextFileList] = useState<UploadFile[]>(
    fileList as any
  )
  useEffect(() => {
    setNextFileList(fileList as any)
  }, [fileList])

  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const translate = useTranslations()

  const beforeUpload = (file: RcFile) => {
    const isLt10M = file.size / 1024 / 1024 < 15
    if (!isLt10M && isShowMessage) {
      message.error(translate('common.errorFileSize'))
      onRemove()
    }
    return isLt10M
  }

  const handleChange: UploadProps['onChange'] = (
    info: UploadChangeParam<UploadFile>
  ) => {
    if (info.file.status === 'uploading') {
      setLoading(true)
    }
    if (info.file.status === 'done') {
      setLoading(false)

      setNextFileList(info.fileList)
      isShowMessage &&
        message.success(
          `${info.file.name} ${translate('common.uploadSuccess')}`
        )
    }
    setNextFileList(info.fileList)
  }

  const uploadImage = async (options) => {
    const { onSuccess, onError, file } = options

    const fmData = new FormData()
    fmData.append('file', file)
    try {
      const res = await axiosInstance.post(endPoint, fmData)
      const data = getResponseData<Media>(res)
      form?.setFieldsValue({ [fieldName]: data?.id })
      if (maxCount > 1) {
        const mediaID = data?.id
        const newMedia = {
          mediaID,
          name: data?.name,
          type: fieldName,
        }
        setMedia([...dataMedia, newMedia])
      } else setMedia(data)

      onSuccess('Ok')
    } catch (err) {
      onError({ err })
      setLoading(false)
    }
  }

  const onRemove = (media?: any) => {
    if (maxCount > 1) {
      const nextMedia = dataMedia?.filter(
        (i) => i?.type === fieldName && i?.mediaID !== media?.uid
      )

      const existMedia = dataMedia?.filter((i) => i?.type !== fieldName)
      setMedia([...existMedia, ...nextMedia])
    } else setMedia(null)
    form?.setFieldsValue({ [fieldName]: null })
  }

  const onPreview = async (file: UploadFile) => {
    // Check if it's an image by MIME type or file extension
    const isImageByType = file?.type?.includes('image/')

    if (isImageByType) {
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj as RcFile)
      }
      setPreviewImage(file.url || (file.preview as string))
      setPreviewOpen(true)
      // setPreviewTitle(
      //   file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1)
      // )
    } else getFileUrl(file?.uid, file?.name)
  }

  const getFileUrl = useCallback(async (mediaID, name) => {
    try {
      const downloadParam = stringify({
        mediaID,
      })

      const {
        data: {
          data: { url },
        },
      } = await axiosInstance.get(`/v1/media/downloads?${downloadParam}`)

      if (url) {
        // Check if file is an image that can be previewed
        const imageExtensions = ['.png', '.jpg', '.jpeg']
        const isImage = imageExtensions.some((ext) =>
          name.toLowerCase().includes(ext)
        )

        if (isImage) {
          setPreviewOpen(true), setPreviewImage(url)
        }
      }
    } catch {
      //do something
    }
  }, [])

  const onDownload = async (file: UploadFile) => {
    const mediaID = dataMedia?.filter(
      (i) => i?.type === fieldName && i?.name === file?.name
    )?.[0]?.mediaID
    if (mediaID)
      try {
        const downloadParam = stringify({
          mediaID,
        })

        const {
          data: {
            data: { url },
          },
        } = await axiosInstance.get(`/v1/media/downloads?${downloadParam}`)

        const link = document.createElement('a')
        if (link) {
          link.href = url
          document.body.appendChild(link)
          link.click()
          link.parentNode.removeChild(link)
        }
      } catch (error) {
        const err = getResponseErrorMessage(error)
        message.error(
          typeof err === 'string' ? err : 'Failed to download image!'
        )
      }
  }

  const showUploadList = {
    showDownloadIcon: isDownload,
    showRemoveIcon: !disabled,
    downloadIcon: <DownloadOutlined />,
  }

  return (
    <div className={className}>
      <Upload
        name="mediaFile"
        beforeUpload={beforeUpload}
        onChange={handleChange}
        customRequest={uploadImage}
        maxCount={maxCount}
        onRemove={onRemove}
        onDownload={onDownload}
        showUploadList={showUploadList}
        onPreview={isOnReview && onPreview}
        fileList={nextFileList}
      >
        <Button
          size={size}
          type={type}
          icon={loading ? <LoadingOutlined /> : <UploadOutlined />}
          disabled={nextFileList?.length === maxCount || disabled}
        >
          {translate('common.upload')}
        </Button>
      </Upload>

      {/* Image component for preview with rotation functionality */}
      {previewImage && (
        <Image
          rootClassName="custom-preview"
          src={previewImage}
          fallback="data:image/png;base64,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"
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => {
              setPreviewOpen(visible)
              if (!visible) {
                setPreviewImage('')
              }
            },
          }}
          style={{ display: 'none' }}
        />
      )}
    </div>
  )
}

export default MediaFileUpload
